import './styles.css';

import { Engine } from './core/engine';
import { refreshEventIndex, warmEventIndex } from './core/events';
import { logInfo, logWarn } from './core/log';
import { KalshiClient } from './kalshi/client';
import { getConnectionStatus, isReady } from './kalshi/connection';
import { App } from './ui/app';
import { installCopy } from './ui/copy';
import { installSeg } from './ui/seg';
import { isUnlocked, relock, renderLock } from './ui/lock';

/** How often to re-check for the extension while the app sits disconnected. */
const RETRY_MS = 3_000;
/** How often to re-check whether the event index has gone stale. */
const INDEX_CHECK_MS = 15 * 60_000;

/** Bring the engine up once the extension is ready (credentials live there), and keep trying until it is. */
function startWhenReady(engine: Engine): void {
  let warned = false;
  const attempt = async (): Promise<void> => {
    if (engine.isStarted) return;
    let ready = false;
    try {
      const s = await getConnectionStatus();
      ready = isReady(s);
      if (ready) engine.setWsUrl(s.wsUrl!);
    } catch { /* extension not answering; try again */ }

    if (!ready) {
      if (!warned) {
        warned = true;
        // Logged once, not every retry; the topbar connection chip already shows the ongoing status.
        logWarn('app', 'waiting for the extension — see the connection chip for what is missing');
      }
      setTimeout(() => void attempt(), RETRY_MS);
      return;
    }

    try {
      await engine.start();
      logInfo('app', 'engine started');
      // Kicked off, never awaited (a cold build is ~10s of paged requests); re-checked on a timer.
      refreshEventIndex(engine.client);
      setInterval(() => refreshEventIndex(engine.client), INDEX_CHECK_MS);
    } catch (e) {
      // Do NOT blank the page: the app stays usable and the chip stays red.
      logWarn('app', `engine failed to start: ${(e as Error).message}`);
      setTimeout(() => void attempt(), RETRY_MS);
    }
  };
  void attempt();
}

async function boot(): Promise<void> {
  const root = document.getElementById('root') as HTMLElement;

  // Curtain first; not security (see ui/lock.ts), just a screen over a half-built trading app.
  (window as unknown as { kg: Record<string, unknown> }).kg = { lock: relock };
  if (!isUnlocked()) await renderLock(root);

  const client = new KalshiClient();
  const engine = new Engine(client);
  installCopy();
  installSeg();
  const app = new App(root, engine);

  // Mount before the engine is up: the grid measures its own width on mount, so it needs a visible container.
  app.mount();

  // Cached rows only (IndexedDB, not network) so this is safe before the extension is up.
  void warmEventIndex();

  // Handy in the console: kg.engine.buildPnlSnapshot(), kg.engine.brackets, …
  (window as unknown as { kg: unknown }).kg = { engine, client, app, lock: relock };

  startWhenReady(engine);
}

void boot();
